// Shared animation variants for consistent, minimalistic motion across the app.
// All animations use GPU-accelerated properties (opacity, transform) only.

// Page-level entrance: subtle fade-up on mount
export const pageVariants = {
  initial: { opacity: 0, y: 14 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.1,
    },
  },
};

// Container that staggers its children
export const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

// Child items in a staggered list
export const staggerItem = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// Self-contained staggered entrance for a grid tile.
//
// `staggerItem` above relies on inheriting the "animate" variant label from a
// `staggerContainer` parent. That inheritance does NOT reach a motion.div that
// sits inside MUI's <Grid container>/<Grid> pair, so such a tile stays at its
// `initial` state — rendered, correctly sized, and invisible at opacity 0.
// Spread this onto the tile's motion.div instead of passing `variants`:
//   <motion.div {...staggerItemProps(i)} style={{ height: '100%' }}>
// The per-item delay is capped so long lists never leave late tiles blank.
export const staggerItemProps = (index = 0) => ({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: 0.3,
    delay: 0.05 + Math.min(index, 12) * 0.06,
    ease: [0.22, 1, 0.36, 1],
  },
});

// Section-level fade (header, content blocks)
export const sectionVariants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// Modal overlay (backdrop)
export const modalOverlayVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: 0.15, ease: 'easeIn' } },
};

// Modal content (the card itself)
export const modalContentVariants = {
  initial: { opacity: 0, scale: 0.97, y: 10 },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    y: 10,
    transition: {
      duration: 0.15,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

// Card hover spring (replaces CSS transition)
export const cardHoverSpring = {
  type: 'spring',
  stiffness: 350,
  damping: 25,
};
